import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, TextField, Button, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { getCustomSelectStyles } from '../../common/styles-select';
import useSettings from '../../hooks/useSettings';
import { PATH_EMAIL_RECIPIENTS } from '../../routes/paths';
import apiClient from '../../config/apiClient';

interface Tenant {
  id: string;
  name: string;
}

const schema = yup.object().shape({
  email: yup.string().email('Please enter a valid email').required('Email is required'),
  tenantId: yup.string().required('Tenant is required'),
});

type FormData = {
  email: string;
  tenantId: string;
};

const CreateEmailRecipientForm = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<{ value: string; label: string } | null>(null);
  const { themeMode } = useSettings();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const navigate = useNavigate();

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const response = await apiClient.get('/tenants');
        setTenants(response.data);
      } catch (error) {
        console.error('Error fetching tenants:', error);
      }
    };

    fetchTenants();
  }, []);

  const onSubmit = async (data: FormData) => {
    try {
      const response = await apiClient.post(`/email/recipients/`, data);

      console.log('Email recipient created:', response.data);

      toast.success('Email recipient created successfully! Redirecting in 5 seconds...', {
        position: 'top-right',
      });

      reset();

      setTimeout(() => {
        navigate(PATH_EMAIL_RECIPIENTS.general.view);
      }, 5000);
    } catch (error) {
      toast.error('Error creating email recipient. Please try again.', {
        position: 'top-right',
      });

      console.error('Error creating email recipient:', error);
    }
  };

  const handleTenantChange = (option: { value: string; label: string } | null) => {
    setSelectedTenant(option);
    setValue('tenantId', option?.value || '');
  };

  const tenantOptions = tenants.map((tenant) => ({
    value: tenant.id,
    label: tenant.name,
  }));

  const customSelectStyles = getCustomSelectStyles(themeMode, true);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      sx={{ maxWidth: 400, margin: '0 auto', mt: 5 }}
    >
      <Typography variant="h4" gutterBottom>
        Create Email Recipient
      </Typography>

      <TextField
        label="Email"
        fullWidth
        margin="normal"
        {...register('email')}
        error={!!errors.email}
        helperText={errors.email?.message}
      />

      <Box sx={{ mt: 2, mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Select Tenant
        </Typography>
        <Select
          options={tenantOptions}
          value={selectedTenant}
          onChange={handleTenantChange}
          styles={customSelectStyles}
          placeholder="Select Tenant"
        />
        {errors.tenantId && <Typography color="error">{errors.tenantId?.message}</Typography>}
      </Box>

      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        sx={{ mt: 3 }}
      >
        Create Email Recipient
      </Button>
    </Box>
  );
};

export default CreateEmailRecipientForm;
